import { CommonModule } from '@angular/common';
import { Component, computed, effect, Inject, inject, Optional, signal, Signal, WritableSignal, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatMenuModule } from '@angular/material/menu';
import { InputDropdownConfig } from '@app/_interfaces/generic.interface';
import { Todo } from '@app/_interfaces/todo.interface';
import { CacheService } from '@app/_services/cache.service';
import { ViewSettingService } from '@app/_services/view-setting.service';
import { TodoStore } from '@app/_stores';
import { InputDropdownComponent } from '@app/components/shared/inputs/input-dropdown/input-dropdown.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { TodoBlockComponent } from '../todo-block/todo-block.component';
import { Entry } from '@app/_models/entry.model';
import { SettingsService } from '@app/_services/settings.service';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { ViewSettingsStore } from '@app/_stores';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { ShowType } from '@app/_types/generic.type';

@Component({
  selector: 'app-unscheduled-todos',
  imports: [
    CommonModule,
    MatMenuModule,
    InputDropdownComponent,
    SvgComponent,
    TodoBlockComponent
  ],
  templateUrl: './unscheduled-todos.component.html',
  styleUrl: './unscheduled-todos.component.scss'
})

export class UnscheduledTodosComponent implements OnInit {

  readonly todoStore = inject(TodoStore);
  readonly viewSettings = inject(ViewSettingsStore);

  // Create writable signal reference for input component (same pattern as today-view)
  readonly unscheduleShowSignal: WritableSignal<ShowType[]> = this.ss.viewSignals()['unscheduleShow'];

  entityShowConfig: Signal<{ [key: string]: InputDropdownConfig }> = computed(() => {
    return this.vs.entityShowConfig();
  });
  hidenValues: string[] = ['habitResponse', 'setup', 'date', 'mood', 'time', 'reminder', 'duration', 'repeat', 'calendarName', 'label', 'emptyDays', 'invalidEntry'];
  filterType: WritableSignal<'all' | 'incomplete' | 'completed' | 'skipped'> = signal('all');
  unscheduledTodos: Signal<Entry[]> = computed(() => {
    const allUnscheduledTodos = this.todoStore.unscheduledTodos();
    const filteredTodos = allUnscheduledTodos.filter(entry => {
      // Look up the todo to check completion status
      const todo = this.todoStore.idToTodo()[entry.setupId];
      const completionStatus = todo?.completions?.[''];
      const isCompleted = completionStatus === 'done';
      const isSkipped = completionStatus === 'skipped';

      switch (this.filterType()) {
        case 'all':
          return true;
        case 'incomplete':
          return !completionStatus;
        case 'completed':
          return isCompleted;
        case 'skipped':
          return isSkipped;
        default:
          return true;
      }
    });

    return filteredTodos.sort((a, b) => a.sortTitle.localeCompare(b.sortTitle));
  })

  screenEnterTime: Signal<Date> = signal(new Date());
  unSubscribe = new Subject<void>();
  public data: { filterType: 'all' | 'incomplete' | 'completed' | 'skipped' };

  constructor(
    @Optional() public dialogRef: MatDialogRef<UnscheduledTodosComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<UnscheduledTodosComponent> | null,
    public vs: ViewSettingService,
    public cc: CacheService,
    public ss: SettingsService,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: { filterType: 'all' | 'incomplete' | 'completed' | 'skipped' } | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: { filterType: 'all' | 'incomplete' | 'completed' | 'skipped' } | null,
    private as: AnalyticsService,
    private storageService: StorageService,
  ) {
    this.data = (dialogData || bottomSheetData)!;
    this.filterType.set(this.data.filterType);

    // Auto-close modal when all unscheduled counts become zero
    effect(() => {
      const uncompletedCount = this.todoStore.unscheduledUncompletedTodosCount();
      const completedCount = this.todoStore.unscheduledcompletedTodosCount();
      const skippedCount = this.todoStore.unscheduledSkippedTodosCount();

      if (uncompletedCount === 0 && completedCount === 0 && skippedCount === 0) {
        this.closeDialog();
      }
    });

    bottomSheetRef?.backdropClick().subscribe(async () => {
      this.closeDialog();
    });
  }

  ngOnInit() {
    this.analyticsInit();
  }

  trackByForEntry(index: number, entry: Entry) {
    return entry.setupId;
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('unscheduled', previousScreen);
      }
    });
  }

  closeDialog() {
    this.as.screenExitEvent('unscheduled', this.screenEnterTime());
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
